
import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import UserChannels from "@/components/UserChannels";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { Building2, Briefcase, GraduationCap, MapPin, Globe, Mail, Linkedin, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const JuliaDeRosiProfile = () => {
  const user = {
    name: "Julia De Rosi",
    role: "GSA Knowledge Network Ambassador",
    title: "Deputy Assistant Commissioner, Design and Construction",
    email: "julia.derosi@gsa.gov",
    linkedIn: "https://www.linkedin.com/in/julia-de-rosi-505a73/",
    location: "Wilmette, Illinois",
    solutionBounties: 3,
    domains: [
      "Federal Real Estate",
      "Public Buildings",
      "Leadership",
      "Project Management",
      "Sustainable Buildings",
      "Historic Properties",
      "Government Operations",
      "Strategic Planning"
    ],
    industries: [
      { name: "Government", icon: Briefcase },
      { name: "Real Estate", icon: Building2 },
      { name: "Construction", icon: Building2 },
      { name: "Sustainability", icon: Globe }
    ],
    confirmedSolutions: 28,
    confirmedKnowledgeBases: 65,
    tokenBalance: 180
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/5eb043bf-469d-46bf-abe8-2651aa4739b3.png"
                    imageAlt="Julia De Rosi"
                  >
                    <Avatar className="h-36 w-36 cursor-pointer">
                      <AvatarImage src="/lovable-uploads/5eb043bf-469d-46bf-abe8-2651aa4739b3.png" alt="Julia De Rosi" />
                      <AvatarFallback className="bg-blue-100 text-blue-800 text-2xl">JD</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <UserChannels />
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div 
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ImageHoverPopup 
                      imageSrc="/lovable-uploads/5eb043bf-469d-46bf-abe8-2651aa4739b3.png"
                      imageAlt="Julia De Rosi"
                    >
                      <h2 className="font-serif text-3xl font-bold text-black cursor-pointer hover:text-primary transition-colors">{user.name}</h2>
                    </ImageHoverPopup>
                    <a href={`mailto:${user.email}`} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                      <Mail size={18} className="mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </a>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="text-lg font-medium text-black">{user.title}</p>
                  </div>
                  <div className="flex items-center mb-4">
                    <p className="text-md text-gray-600">{user.role}</p>
                    <Link to="/gsa-channel" className="flex items-center hover:opacity-90 ml-2">
                      <img
                        src="/lovable-uploads/3e0cc269-0dd5-40b5-a1f0-0ebbe7e85733.png"
                        alt="GSA"
                        className="h-10 w-10 hover:scale-110 transition-transform duration-200"
                      />
                    </Link>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="h-4 w-4 text-gray-600" />
                    <span className="text-gray-600">{user.location}</span>
                    <a 
                      href={user.linkedIn} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 ml-2"
                    >
                      <Linkedin size={16} />
                      <ExternalLink size={14} className="ml-1" />
                    </a>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link 
                          to="/solution-bounties/julia"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={8.7} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Education</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <GraduationCap className="h-5 w-5 text-gray-600 mr-2 mt-1" />
                  <div>
                    <p className="font-medium">MBA</p>
                    <p className="text-gray-600">Northwestern University - Kellogg School of Management (2009-2011)</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <GraduationCap className="h-5 w-5 text-gray-600 mr-2 mt-1" />
                  <div>
                    <p className="font-medium">Master's Certificate in Project Management</p>
                    <p className="text-gray-600">The George Washington University School of Business (2006-2008)</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <GraduationCap className="h-5 w-5 text-gray-600 mr-2 mt-1" />
                  <div>
                    <p className="font-medium">Bachelor of Business Administration</p>
                    <p className="text-gray-600">Loyola University Chicago</p>
                    <p className="text-gray-600 text-sm">Business, Marketing, Human Resources, Int'l Business</p>
                    <p className="text-gray-600 text-sm">Minor in Photography</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Personal</h3>
              <p className="text-gray-700">
                Julia has lived abroad and is a world traveler, speaks 3 languages and has visited 26 countries. 
                She loves photography, has recently made the conversion to paddle and is a wannabe yogi. 
                Julia currently resides in Wilmette with her husband, two kids, dog, and five (formerly six) resilient fish.
              </p>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge 
                    key={domain} 
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default JuliaDeRosiProfile;
